/**
 * Data Manager using Firebase (v3)
 * Replaces LocalStorage synchronous calls with Async Firebase calls.
 */

import { FirebaseService } from './firebaseService.js';
import { sanitizeText } from '../utils/sanitize.js';
import { analyzeClientTexts, extractKeywords as advancedExtractKeywords, summarizeThemes, generateCustomerReportSummary } from '../utils/textAnalysis.js';

// --- KNOWLEDGE BASE (STATIC) ---
export const KB_DATABASE = {
    dsm: [
        {
            keywords: ['sentire voci', 'sento voci', 'mi parlano', 'visioni mistiche', 'non c\'è nessuno ma sento'],
            message: 'Possibili sintomi allucinatori. Consultare DSM-5 (Disturbi dello Spettro della Schizofrenia).',
            level: 'warning',
            source: 'DSM-5',
            justification: 'Rilevata descrizione di percezioni sensoriali in assenza di stimoli esterni (allucinazioni uditive/visive).',
            reference: 'APA (2013). DSM-5. Criterio A Schizofrenia.',
            criterion: 'Allucinazioni'
        },
        {
            keywords: ['taglio con lametta', 'mi taglio', 'vedere il sangue', 'farmi del male', 'mi brucio la pelle'],
            message: 'Rischio autolesionismo. Valutare invio a specialista.',
            level: 'danger',
            source: 'DSM-5',
            justification: 'Riferimenti espliciti a comportamenti autolesivi non suicidari (NSSI).',
            reference: 'APA (2013). DSM-5. Condizioni che necessitano ulteriori studi.',
            criterion: 'Autolesionismo Non Suicidario'
        },
        {
            keywords: ['mi voglio uccidere', 'farla finita', 'non voglio più svegliarmi', 'medito il suicidio', 'impiccarmi'],
            message: 'RISCHIO SUICIDARIO. Attivare protocollo di emergenza/invio immediato.',
            level: 'danger',
            source: 'Psichiatria d\'Urgenza',
            justification: 'Espressioni che indicano ideazione o progettualità suicidaria.',
            reference: 'Linee Guida Prevenzione Suicidio.',
            criterion: 'Ideazione Suicidaria'
        },
        {
            keywords: ['senso di vuoto', 'paura dell\'abbandono', 'tutti mi lasciano', 'relazioni instabili', 'disperato per amore'],
            message: 'Possibili tratti Borderline/Dipendenza. Verificare criteri.',
            level: 'warning',
            source: 'DSM-5',
            justification: 'Pattern linguistici indicativi di timore di abbandono e instabilità relazionale.',
            reference: 'DSM-5, Disturbo Borderline di Personalità.',
            criterion: 'Sforzi disperati di evitare un abbandono'
        },
        {
            keywords: ['attacco di panico', 'cuore che scoppia', 'paura di morire', 'sudorazione improvvisa', 'tremore incontrollabile'],
            message: 'Sintomatologia ansiosa / Possibile Attacco di Panico.',
            level: 'info',
            source: 'DSM-5',
            justification: 'Sintomi somatici compatibili con arousal simpatico elevato o Attacco di Panico.',
            reference: 'DSM-5, Disturbo di Panico.',
            criterion: 'Attacco di Panico'
        },
        {
            keywords: ['vomito autoindotto', 'digiuno per dimagrire', 'trocco grassa', 'controllo le calorie', 'abbuffata incontrollata'],
            message: 'Possibile Disturbo Alimentare (DCA). Monitorare.',
            level: 'warning',
            source: 'DSM-5',
            justification: 'Riferimenti a restrizione calorica, abbuffate o distorsione immagine corporea.',
            reference: 'DSM-5, Anoressia/Bulimia/BED.',
            criterion: 'Alterazione dell\'immagine corporea / Comportamenti di compensazione'
        }
    ],
    theory: [
        // GESTALT
        {
            keywords: ['ho sognato', 'stanotte ho sognato', 'incubo ricorrente', 'sogno strano'],
            message: 'Lavoro Onirico (Gestalt).',
            level: 'info',
            source: 'Gestalt Therapy',
            justification: 'Il sogno è proiezione di parti scisse del Sé (messaggio esistenziale).',
            reference: 'Perls, F. (1969). Gestalt Therapy Verbatim.',
            application: 'Tecnica: Identificazione con elementi del sogno ("Sii il sogno")'
        },
        {
            keywords: ['tutti pensano che', 'lui è arrabbiato con me', 'ce l\'hanno con me', 'mi giudicano tutti'],
            message: 'Meccanismo di Proiezione.',
            level: 'info',
            source: 'Gestalt / Psicoanalisi',
            justification: 'Attribuzione all\'esterno di propri tratti, emozioni o giudizi non riconosciuti.',
            reference: 'Perls, Hefferline, Goodman (1951).',
            application: 'Tecnica: Riacquisizione della proiezione (Inversione pronominale)'
        },
        {
            keywords: ['non sento niente', 'mi isolo dal mondo', 'non voglio vedere nessuno', 'distante da tutto'],
            message: 'Interruzione di Contatto (Ritiro/Isolamento).',
            level: 'info',
            source: 'Gestalt Therapy',
            justification: 'Difficoltà nel ciclo di contatto (fase di pieno contatto o pre-contatto).',
            reference: 'Polster (1973).',
            application: 'Lavorare sulla consapevolezza sensoriale e sul "qui e ora"'
        },
        // ANALISI TRANSAZIONALE (AT)
        {
            keywords: ['devi assolutamente', 'bisogna sempre', 'le regole sono regole', 'è proibito'],
            message: 'Stato dell\'Io Genitore (Normativo o Critico).',
            level: 'info',
            source: 'Analisi Transazionale',
            justification: 'Uso di imperativi categorici, generalizzazioni e regole morali introiettate.',
            reference: 'Berne, E. (1961). Transactional Analysis in Psychotherapy.',
            application: 'Analisi Funzionale Stati dell\'Io (Egogramma)'
        },
        {
            keywords: ['non ci riesco', 'è colpa mia', 'aiutami tu', 'non so cosa fare'],
            message: 'Stato dell\'Io Bambino (Adattato o Vittima).',
            level: 'info',
            source: 'Analisi Transazionale',
            justification: 'Linguaggio passivo, richiesta di accudimento o espressione di impotenza.',
            reference: 'Berne, E. (1964). Games People Play (A che gioco giochiamo).',
            application: 'Potenziamento dell\'Adulto e protezione'
        },
        {
            keywords: ['capita sempre a me', 'destino crudele', 'come mia madre', 'storia che si ripete'],
            message: 'Analisi del Copione di Vita.',
            level: 'info',
            source: 'Analisi Transazionale',
            justification: 'Riferimento a schemi ripetitivi di vita (Script).',
            reference: 'Steiner, C. (1974). Scripts People Live.',
            application: 'Ridecisione del copione'
        },
        // ALTRI (Rogers, CBT)
        {
            keywords: ['mai nessuno', 'sempre tutti', 'assolutamente impossibile', 'devastante'],
            message: 'Distorsione Cognitiva: Generalizzazione / Pensiero "Tutto o Nulla".',
            level: 'info',
            source: 'CBT (Cognitive Behavioral Therapy)',
            justification: 'Pensiero dicotomico o ipergeneralizzazione che causa sofferenza.',
            reference: 'Beck, A.T. (1976). Cognitive Therapy and the Emotional Disorders.',
            application: 'Ristrutturazione cognitiva (esame di realtà)'
        },
        {
            keywords: ['provo attrazione', 'odio il terapeuta', 'mi ricordi mio padre', 'innamorata del counselor'],
            message: 'Dinamica Transferale. ATTENZIONE.',
            level: 'warning',
            source: 'Psicoanalisi',
            justification: 'Spostamento di sentimenti/figure del passato sulla figura del counselor.',
            reference: 'Freud (1912). Tecnica della Psicoanalisi.',
            application: 'Supervisione obbligatoria. Non agire il controtransfert.'
        },
        // BIOENERGETICA (Lowen)
        {
            keywords: ['respiro bloccato', 'peso sul petto', 'gambe rigide', 'mascella serrata', 'non sento il corpo'],
            message: 'Blocco Energetico / Corazza Caratteriale.',
            level: 'info',
            source: 'Bioenergetica (Lowen)',
            justification: 'Manifestazione somatica di tensioni emotive croniche (Corazza Muscolare).',
            reference: 'Lowen, A. (1975). Bioenergetica.',
            application: 'Esercizi di Grounding e lavoro sul respiro.'
        },
        {
            keywords: ['non sento i piedi', 'testa tra le nuvole', 'mi sento sradicato', 'perso nel vuoto'],
            message: 'Mancanza di Grounding (Radicamento).',
            level: 'info',
            source: 'Bioenergetica',
            justification: 'Disconnessione dalla realtà fisica e dal proprio sostegno corporeo.',
            reference: 'Lowen, A., & Lowen, L. (1977). Espansione e integrazione del corpo.',
            application: 'Esercizi di radicamento (contatto piedi-terra, bending).'
        }
    ],
    ethics: [
        {
            keywords: ['ti prescrivo', 'consiglio il farmaco', 'prendi questo farmaco', 'dosaggio consigliato', 'sospenda la terapia'],
            message: 'Alert Deontologico: Il Counselor NON prescrive farmaci.',
            level: 'danger',
            source: 'Codice Deontologico',
            justification: 'Abuso della professione medica/psichiatrica. Art. 348 C.P. Il counselor non deve mai suggerire o modificare terapie farmacologiche.',
            reference: 'Legge 3/2018; AssoCounseling.',
            criterion: 'Limite professionale invalicabile'
        },
        {
            keywords: ['diagnosi di', 'sei affetto da', 'la tua patologia è', 'soffri di disturbo'],
            message: 'Alert: Il Counselor opera nella Salutogenesi, non fa diagnosi nosografiche.',
            level: 'warning',
            source: 'AssoCounseling',
            justification: 'Il focus è sulle risorse e sul benessere, non sulla cura della patologia.',
            reference: 'Legge 4/2013.',
            criterion: 'Linguaggio non patologizzante'
        }
    ]
};

// --- DATA MANAGER IMPLEMENTATION ---

export const DataManager = {

    getKB: () => KB_DATABASE,

    summarizeThemes,
    generateCustomerReportSummary,
    analyzeClientTexts,

    // --- CREATIVE WORKS ---
    saveCreativeWork: async (workData) => {
        return await FirebaseService.saveCreativeWork(workData);
    },

    getCreativeWorks: async (clientId) => {
        return await FirebaseService.getCreativeWorksByClient(clientId);
    },

    extractKeywords: (text) => {
        if (!text) return [];
        // Use advanced extraction and return keys (tags)
        const keywordMap = advancedExtractKeywords(text);
        return Object.keys(keywordMap);
    },

    analyzeText: (text) => {
        if (!text) return [];
        const alerts = [];
        // Normalize spaces to allow phrase matching
        const normalizedText = text.toLowerCase().replace(/\s+/g, ' ');

        const check = (category, list) => {
            list.forEach(item => {
                // Find all phrases that appear in the text
                const matches = item.keywords.filter(phrase => normalizedText.includes(phrase.toLowerCase()));

                if (matches.length > 0 && !alerts.some(a => a.message === item.message)) {
                    alerts.push({
                        ...item,
                        category,
                        matches,
                        level: item.level || 'info' // Ensure level is never undefined
                    });
                }
            });
        };

        check('DSM-5', KB_DATABASE.dsm);
        check('Theory', KB_DATABASE.theory);
        check('Ethics', KB_DATABASE.ethics);
        return alerts;
    },


    analyzeTextByCategory: (text, categories = ['dsm', 'theory', 'ethics']) => {
        if (!text) return [];
        const alerts = [];
        const lowerText = text.toLowerCase();

        if (categories.includes('dsm')) {
            KB_DATABASE.dsm.forEach(item => {
                if (item.keywords.some(k => lowerText.includes(k.toLowerCase())) && !alerts.some(a => a.message === item.message))
                    alerts.push({ ...item, category: 'DSM-5' });
            });
        }
        if (categories.includes('theory')) {
            KB_DATABASE.theory.forEach(item => {
                if (item.keywords.some(k => lowerText.includes(k.toLowerCase())) && !alerts.some(a => a.message === item.message))
                    alerts.push({ ...item, category: 'Theory' });
            });
        }
        if (categories.includes('ethics')) {
            KB_DATABASE.ethics.forEach(item => {
                if (item.keywords.some(k => lowerText.includes(k.toLowerCase())) && !alerts.some(a => a.message === item.message))
                    alerts.push({ ...item, category: 'Ethics' });
            });
        }
        return alerts;
    },

    // --- CLIENTS (ASYNC) ---

    createClient: async (nameOrData, email) => {
        let clientPayload;

        // Default properties
        const defaults = {
            mode: 'manual',
            isActive: true,
            contract: {
                title: "Nuovo Percorso",
                description: "Definisci il tuo obiettivo qui...",
                startDate: new Date().toISOString()
            },
            keywords: {}
        };

        if (typeof nameOrData === 'object' && nameOrData !== null) {
            clientPayload = { ...defaults, ...nameOrData };
        } else {
            clientPayload = {
                ...defaults,
                name: nameOrData,
                email: email
            };
        }

        return await FirebaseService.createClient(clientPayload);
    },

    /**
     * Metodo primario per il login cliente.
     * Lookup O(1) diretto per UID Firebase Auth.
     * Controlla anche l'onboarding implicito (compatibilità dati legacy).
     */
    getClientByUid: async (uid) => {
        const client = await FirebaseService.getClientByUid(uid);

        if (client) {
            // "Implicit Validation" per dati legacy:
            // Se il flag esplicito manca, consenti accesso se hanno dati validi
            if (client.hasCompletedOnboarding === undefined) {
                console.log(`[DataManager] checking implicit onboarding for uid=${uid}...`);

                const [t0, works, meetings] = await Promise.all([
                    DataManager.getLatestT0(client.id),
                    DataManager.getClientWorks(client.id),
                    DataManager.getClientMeetings(client.id)
                ]);

                const hasData = !!t0 || works.length > 0 || meetings.length > 0;
                console.log(`[DataManager] Onboarding check: T0=${!!t0}, Works=${works.length}, Meetings=${meetings.length} => ${hasData}`);

                client.hasCompletedOnboarding = hasData;

                if (hasData) {
                    DataManager.updateClient(client.id, { hasCompletedOnboarding: true }).catch(console.error);
                }
            }
        }

        return client;
    },

    /**
     * Recupera cliente per email (usato anche per linking al primo accesso)
     */
    getClientByEmail: async (email) => {
        return await FirebaseService.getClientByEmail(email);
    },

    getClient: async (email) => {
        const client = await FirebaseService.getClientByEmail(email);

        if (client) {
            if (client.hasCompletedOnboarding === undefined) {
                console.log(`[DataManager] checking implicit onboarding for ${client.email}...`);

                const [t0, works, meetings] = await Promise.all([
                    DataManager.getLatestT0(client.id),
                    DataManager.getClientWorks(client.id),
                    DataManager.getClientMeetings(client.id)
                ]);

                const hasData = !!t0 || works.length > 0 || meetings.length > 0;

                console.log(`[DataManager] Check results: T0=${!!t0}, Works=${works.length}, Meetings=${meetings.length} => Onboarded=${hasData}`);

                client.hasCompletedOnboarding = hasData;

                if (hasData) {
                    DataManager.updateClient(client.id, { hasCompletedOnboarding: true }).catch(console.error);
                }
            }
        }

        return client;
    },

    getClientById: async (id) => {
        return await FirebaseService.getClientById(id);
    },

    getCounselorByUid: async (uid) => {
        return await FirebaseService.getCounselorByUid(uid);
    },

    getCounselorByEmail: async (email) => {
        return await FirebaseService.getCounselorByEmail(email);
    },

    getAllCounselors: async () => {
        return await FirebaseService.getAllCounselors();
    },

    getAllCounselorsWithStats: async () => {
        return await FirebaseService.getAllCounselorsWithStats();
    },

    createCounselor: async (uid, email, name, role = 'counselor', extraData = {}) => {
        return await FirebaseService.createCounselor(uid, email, name, role, extraData);
    },

    updateCounselor: async (uid, updateData) => {
        return await FirebaseService.updateCounselor(uid, updateData);
    },

    deleteCounselor: async (uid) => {
        return await FirebaseService.deleteCounselor(uid);
    },

    reassignClientCounselor: async (clientId, newCounselorId, newCounselorEmail = '', newCounselorName = '') => {
        return await FirebaseService.reassignClientCounselor(clientId, newCounselorId, newCounselorEmail, newCounselorName);
    },

    getClients: async (filterCounselorId = null) => {
        return await FirebaseService.getAllClients(filterCounselorId);
    },

    getClientContracts: (client) => {
        if (!client) return [];
        if (Array.isArray(client.contracts) && client.contracts.length > 0) {
            return client.contracts;
        }
        if (client.contract && (client.contract.title || client.contract.description || client.contract.startDate)) {
            return [{
                id: client.contract.id || 'contract_legacy',
                title: client.contract.title || 'Contratto di Counseling',
                description: client.contract.description || '',
                startDate: client.contract.startDate || '',
                endDate: client.contract.endDate || '',
                status: client.contract.status || 'attivo',
                createdAt: client.contract.createdAt || new Date().toISOString()
            }];
        }
        return [];
    },

    saveClientContract: async (clientId, contractData) => {
        const client = await DataManager.getClientById(clientId);
        if (!client) return null;
        const currentContracts = DataManager.getClientContracts(client);
        let updatedContracts = [];
        
        if (contractData.id) {
            // Update existing
            const exists = currentContracts.some(c => c.id === contractData.id);
            if (exists) {
                updatedContracts = currentContracts.map(c => c.id === contractData.id ? { ...c, ...contractData, updatedAt: new Date().toISOString() } : c);
            } else {
                updatedContracts = [...currentContracts, { ...contractData, updatedAt: new Date().toISOString() }];
            }
        } else {
            // Create new
            const newContract = {
                id: `contract_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
                ...contractData,
                status: contractData.status || 'attivo',
                createdAt: new Date().toISOString()
            };
            updatedContracts = [...currentContracts, newContract];
        }

        // Active contract for backward compatibility
        const activeContract = updatedContracts.find(c => c.status === 'attivo') || updatedContracts[updatedContracts.length - 1] || null;

        await FirebaseService.updateClient(clientId, {
            contracts: updatedContracts,
            contract: activeContract ? { title: activeContract.title, description: activeContract.description, startDate: activeContract.startDate, id: activeContract.id } : null
        });

        return updatedContracts;
    },

    deleteClientContract: async (clientId, contractId) => {
        const client = await DataManager.getClientById(clientId);
        if (!client) return null;
        const currentContracts = DataManager.getClientContracts(client);
        const updatedContracts = currentContracts.filter(c => c.id !== contractId);
        const activeContract = updatedContracts.find(c => c.status === 'attivo') || updatedContracts[updatedContracts.length - 1] || null;

        await FirebaseService.updateClient(clientId, {
            contracts: updatedContracts,
            contract: activeContract ? { title: activeContract.title, description: activeContract.description, startDate: activeContract.startDate, id: activeContract.id } : null
        });

        return updatedContracts;
    },

    updateClientContract: async (clientId, contractData) => {
        return await DataManager.saveClientContract(clientId, contractData);
    },

    updateClient: async (clientId, data) => {
        return await FirebaseService.updateClient(clientId, data);
    },

    updateClientMode: async (clientId, mode, isActive) => {
        const payload = { mode };
        if (typeof isActive === 'boolean') {
            payload.isActive = isActive;
        }
        return await FirebaseService.updateClient(clientId, payload);
    },

    updateClientWheelLabels: async (clientId, labels) => {
        return await FirebaseService.updateClient(clientId, { wheelLabels: labels });
    },

    // --- SUPERADMIN ACCESS AUTHORIZATION (DEONTOLOGICAL PRIVACY) ---
    requestSuperAdminAccess: async (clientId, requesterInfo) => {
        return await FirebaseService.requestSuperAdminAccess(clientId, requesterInfo);
    },

    approveSuperAdminAccess: async (clientId, approverInfo) => {
        return await FirebaseService.approveSuperAdminAccess(clientId, approverInfo);
    },

    rejectSuperAdminAccess: async (clientId, rejectorInfo) => {
        return await FirebaseService.rejectSuperAdminAccess(clientId, rejectorInfo);
    },

    revokeSuperAdminAccess: async (clientId, revokerInfo) => {
        return await FirebaseService.revokeSuperAdminAccess(clientId, revokerInfo);
    },

    cancelSuperAdminAccessRequest: async (clientId) => {
        return await FirebaseService.cancelSuperAdminAccessRequest(clientId);
    },

    getAllSuperAdminAccessLogs: async (clientsList = null) => {
        return await FirebaseService.getAllSuperAdminAccessLogs(clientsList);
    },

    // --- SYNTHESIS VERSIONING ---

    /**
     * Save a new version of the synthesis
     * @param {string} clientId - Client ID
     * @param {string} content - Synthesis content (markdown)
     * @param {string} label - Version label ('auto' | 'manual' | 'tuned')
     * @returns {Promise<string>} Version ID
     */
    saveSynthesisVersion: async (clientId, content, label = 'auto') => {
        const client = await FirebaseService.getClientById(clientId);
        if (!client) throw new Error('Client not found');

        const version = {
            id: `synthesis_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            timestamp: new Date().toISOString(),
            content,
            label, // 'auto', 'manual', 'tuned'
            wordCount: content.split(/\s+/).length
        };

        const versions = client.synthesisVersions || [];
        versions.push(version);

        // Keep only last 20 versions to avoid bloat
        const trimmedVersions = versions.slice(-20);

        await FirebaseService.updateClient(clientId, {
            synthesisVersions: trimmedVersions,
            lastAiSummary: content // Keep backward compatibility
        });

        return version.id;
    },

    /**
     * Get all synthesis versions for a client
     * @param {string} clientId - Client ID
     * @returns {Promise<Array>} Array of versions (newest first)
     */
    getSynthesisVersions: async (clientId) => {
        const client = await FirebaseService.getClientById(clientId);
        if (!client) return [];

        const versions = client.synthesisVersions || [];
        return versions.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    },

    /**
     * Get a specific synthesis version
     * @param {string} clientId - Client ID
     * @param {string} versionId - Version ID
     * @returns {Promise<Object|null>} Version object or null
     */
    getSynthesisVersion: async (clientId, versionId) => {
        const versions = await DataManager.getSynthesisVersions(clientId);
        return versions.find(v => v.id === versionId) || null;
    },

    /**
     * Restore a synthesis version as current
     * @param {string} clientId - Client ID
     * @param {string} versionId - Version ID to restore
     * @returns {Promise<void>}
     */
    restoreSynthesisVersion: async (clientId, versionId) => {
        const version = await DataManager.getSynthesisVersion(clientId, versionId);
        if (!version) throw new Error('Version not found');

        // Create a new version from the restored one
        await DataManager.saveSynthesisVersion(clientId, version.content, 'restored');
    },

    /**
     * Update a specific synthesis version
     * @param {string} clientId - Client ID
     * @param {string} versionId - Version ID
     * @param {string} newContent - New content
     * @returns {Promise<void>}
     */
    updateSynthesisVersion: async (clientId, versionId, newContent) => {
        const client = await FirebaseService.getClientById(clientId);
        if (!client) throw new Error('Client not found');

        const versions = client.synthesisVersions || [];
        const versionIndex = versions.findIndex(v => v.id === versionId);

        if (versionIndex === -1) throw new Error('Version not found');

        versions[versionIndex].content = newContent;
        versions[versionIndex].editedAt = new Date().toISOString();
        versions[versionIndex].wordCount = newContent.split(/\s+/).length;

        await FirebaseService.updateClient(clientId, {
            synthesisVersions: versions,
            lastAiSummary: newContent // Update current if it's the latest
        });
    },

    /**
     * Delete a specific synthesis version
     * @param {string} clientId - Client ID
     * @param {string} versionId - Version ID to delete
     * @returns {Promise<void>}
     */
    deleteSynthesisVersion: async (clientId, versionId) => {
        const client = await FirebaseService.getClientById(clientId);
        if (!client) throw new Error('Client not found');

        const versions = (client.synthesisVersions || []).filter(v => v.id !== versionId);

        await FirebaseService.updateClient(clientId, {
            synthesisVersions: versions
        });
    },

    // --- GUIDED SYNTHESIS QUESTIONNAIRE ---

    /**
     * Save questionnaire answers for a client
     * @param {string} clientId - Client ID
     * @param {Object} data - Questionnaire data { answers, confirmationStatus, lastUpdated }
     * @returns {Promise<void>}
     */
    saveQuestionnaireAnswers: async (clientId, data) => {
        return await FirebaseService.updateClient(clientId, {
            questionnaireData: {
                answers: data.answers || {},
                confirmationStatus: data.confirmationStatus || {},
                lastUpdated: data.lastUpdated || new Date().toISOString()
            }
        });
    },

    /**
     * Get questionnaire answers for a client
     * @param {string} clientId - Client ID
     * @returns {Promise<Object|null>} Questionnaire data or null
     */
    getQuestionnaireAnswers: async (clientId) => {
        const client = await FirebaseService.getClientById(clientId);
        if (!client) return null;

        return client.questionnaireData || null;
    },

    /**
     * Update a single questionnaire answer
     * @param {string} clientId - Client ID
     * @param {string} questionId - Question ID
     * @param {string} answer - Answer text
     * @param {string} status - Confirmation status
     * @returns {Promise<void>}
     */
    updateQuestionnaireAnswer: async (clientId, questionId, answer, status = 'editing') => {
        const data = await DataManager.getQuestionnaireAnswers(clientId);
        const currentData = data || { answers: {}, confirmationStatus: {} };

        currentData.answers[questionId] = answer;
        currentData.confirmationStatus[questionId] = status;
        currentData.lastUpdated = new Date().toISOString();

        return await DataManager.saveQuestionnaireAnswers(clientId, currentData);
    },

    /**
     * Confirm a questionnaire answer
     * @param {string} clientId - Client ID
     * @param {string} questionId - Question ID
     * @returns {Promise<void>}
     */
    confirmQuestionnaireAnswer: async (clientId, questionId) => {
        const data = await DataManager.getQuestionnaireAnswers(clientId);
        if (!data) return;

        data.confirmationStatus[questionId] = 'confirmed';
        data.lastUpdated = new Date().toISOString();

        return await DataManager.saveQuestionnaireAnswers(clientId, data);
    },

    deleteClient: async (clientId) => {
        return await FirebaseService.deleteClient(clientId);
    },

    changeClientId: async (oldId, newId) => {
        return await FirebaseService.changeClientId(oldId, newId);
    },

    // --- WORKS (ASYNC) ---

    saveWork: async (arg1, arg2, arg3, arg4, arg5) => {
        let clientId, type, content, shared, timestamp;
        let extras = {};

        // 1. Single Object Pattern (e.g. FrequencyGenerator)
        if (typeof arg1 === 'object' && arg1.clientId) {
            ({ clientId, type, content, shared, timestamp, ...extras } = arg1);
        }
        // 2. ID + Config Object Pattern (e.g. ClientView)
        else if (typeof arg1 === 'string' && typeof arg2 === 'object' && (arg2.content || arg2.type)) {
            clientId = arg1;
            ({ type, content, shared, timestamp, ...extras } = arg2);
        }
        // 3. Standard Arguments Pattern
        else {
            clientId = arg1;
            type = arg2;
            content = arg3;
            shared = arg4 || false;
            timestamp = arg5;
        }

        // Default shared to false if undefined
        if (shared === undefined) shared = false;

        // NLP Analysis preparation
        let textToAnalyze = "";
        if (typeof content === 'string') {
            textToAnalyze = content;
        } else if (content && typeof content === 'object') {
            textToAnalyze = (content.title ? content.title + " " : "") + (content.text || "");
            if (content.answers) {
                Object.values(content.answers).forEach(val => textToAnalyze += " " + val);
            }
        }

        const extractedTags = DataManager.extractKeywords(textToAnalyze);

        // autoTags injection (handle string vs object content)
        if (content && typeof content === 'object') {
            content.autoTags = extractedTags;
        }

        const workPayload = {
            clientId,
            type,
            content,
            shared,
            ...extras
        };
        if (timestamp) workPayload.timestamp = timestamp;

        const newWork = await FirebaseService.saveWork(workPayload);

        // Update Client Semantic Map
        await DataManager.updateClientKeywords(clientId, extractedTags, [], type);

        return newWork;
    },

    getWorkById: async (workId) => {
        return await FirebaseService.getWorkById(workId);
    },

    updateWork: async (workId, updateDataOrContent, shared) => {
        let updateData = updateDataOrContent;
        if (typeof updateDataOrContent === 'string') {
            updateData = { content: updateDataOrContent };
            if (shared !== undefined) updateData.shared = shared;
        }
        return await FirebaseService.updateWork(workId, updateData);
    },

    deleteWork: async (workId) => {
        return await FirebaseService.deleteWork(workId);
    },

    getClientWorks: async (clientId, counselorView = false) => {
        return await FirebaseService.getWorksByClient(clientId, counselorView);
    },

    // --- MEETINGS (ASYNC) ---

    createMeeting: async (arg1, arg2, arg3, arg4, arg5, arg6, arg7, arg8) => {
        let payload = {};

        if (typeof arg1 === 'object' && arg1 !== null) {
            // Object signature: createMeeting({ clientId, date, notes, ... })
            payload = { ...arg1 };
        } else {
            // Positional signature: createMeeting(clientId, date, notes, emoji, tags, observations, counselorFeeling, contractId)
            payload = {
                clientId: arg1,
                date: arg2,
                counselorNotes: arg3,
                counselorEmoji: arg4,
                counselorTags: arg5,
                observations: arg6,
                counselorFeeling: arg7,
                contractId: arg8 || null
            };
        }

        // Ensure defaults
        if (!payload.date) payload.date = new Date().toISOString();
        if (!payload.clientFeedback) payload.clientFeedback = null;
        if (!payload.contractId && typeof arg1 === 'object' && arg1.contractId) payload.contractId = arg1.contractId;

        const newMeeting = await FirebaseService.saveMeeting(payload);

        // Refresh the Semantic Map (keywords) whenever a meeting is created
        await DataManager.analyzeAndUpdateKeywords(payload.clientId);

        return newMeeting;
    },

    // Alias for compatibility
    saveMeeting: async (payload) => {
        return DataManager.createMeeting(payload);
    },

    getMeetingById: async (meetingId) => {
        return await FirebaseService.getMeetingById(meetingId);
    },

    updateMeeting: async (meetingId, date, notes, tags, observations = {}, emoji = null, counselorFeeling = null, contractId = undefined) => {
        let payload = {};
        if (typeof date === 'object' && date !== null) {
            payload = { ...date };
        } else {
            payload = {
                date,
                counselorNotes: notes,
                notes: notes,
                counselorTags: tags,
                tags: tags,
                observations: observations,
                counselorFeeling: counselorFeeling
            };
            if (emoji) payload.counselorEmoji = emoji;
            if (contractId !== undefined) payload.contractId = contractId;
        }

        const result = await FirebaseService.updateMeeting(meetingId, payload);
        
        // Refresh keywords for the client safely
        try {
            const meeting = await FirebaseService.getMeetingById(meetingId);
            const clientId = payload.clientId || meeting?.clientId;
            if (clientId) {
                await DataManager.analyzeAndUpdateKeywords(clientId);
            }
        } catch (kwErr) {
            console.warn("Could not refresh keywords after meeting update:", kwErr);
        }

        return result;
    },

    getClientMeetings: async (clientId, includeDeleted = false, clientEmail = null, clientName = null, clientDoc = null) => {
        let meetings = [];
        try {
            meetings = await FirebaseService.getMeetingsByClient(clientId, clientEmail, clientName);
        } catch (err) {
            console.warn("Could not fetch meetings via FirebaseService:", err);
        }

        // Check embedded meetings on clientDoc if query returned empty
        if ((!meetings || meetings.length === 0) && clientDoc) {
            if (Array.isArray(clientDoc.meetings) && clientDoc.meetings.length > 0) {
                meetings = clientDoc.meetings.map((m, idx) => ({
                    ...m,
                    id: m.id || m._id || `embedded_${clientId}_${idx}`
                }));
            }
        }

        if (includeDeleted) return meetings;
        return (meetings || []).filter(m => !m.deletedAt);
    },

    getAllMeetings: async () => {
        return await FirebaseService.getAllMeetings();
    },

    softDeleteMeeting: async (meetingId) => {
        return await FirebaseService.updateMeeting(meetingId, {
            deletedAt: new Date().toISOString()
        });
    },

    restoreMeeting: async (meetingId) => {
        return await FirebaseService.updateMeeting(meetingId, {
            deletedAt: null
        });
    },

    deleteMeeting: async (meetingId) => {
        return await FirebaseService.deleteMeeting(meetingId);
    },

    // --- PUBLIC INSIGHTS ---

    savePublicInsight: async (insightData) => {
        return await FirebaseService.savePublicInsight(insightData);
    },

    getPublicInsightsForCounselor: async (counselorId) => {
        return await FirebaseService.getPublicInsightsForCounselor(counselorId);
    },

    acceptPublicInsight: async (insight, clientId) => {
        try {
            const author = insight.name || insight.clientName || 'Cliente';
            // Create a work entry
            await DataManager.saveWork(
                clientId,
                'insight_cliente',
                {
                    title: `Insight di ${author} (dal modulo pubblico)`,
                    text: insight.notes,
                    email: insight.email,
                    authorName: author
                },
                false, // Non condiviso
                insight.timestamp
            );
            
            // Delete the processed insight
            await FirebaseService.deletePublicInsight(insight.id);
            return true;
        } catch (error) {
            console.error("Error accepting public insight:", error);
            throw error;
        }
    },

    rejectPublicInsight: async (insightId) => {
        try {
            await FirebaseService.deletePublicInsight(insightId);
            return true;
        } catch (error) {
            console.error("Error rejecting public insight:", error);
            throw error;
        }
    },

    mergeClientTags: async (clientId, oldTagsArray, newTag) => {
        const meetings = await DataManager.getClientMeetings(clientId, false);
        let updatedCount = 0;
        const lowerOldTags = oldTagsArray.map(t => t.toLowerCase().trim());
        
        for (const meeting of meetings) {
            const rawTags = meeting.counselorTags || meeting.tags || '';
            if (!rawTags) continue;
            
            let tags = rawTags.split(',').map(t => t.trim());
            let modified = false;
            
            tags = tags.map(t => {
                let cleanT = t.replace(/[.,;:!?]+$/, '').trim(); // Clean trailing punctuation
                if (cleanT !== t) modified = true;
                
                if (lowerOldTags.includes(cleanT.toLowerCase())) {
                    modified = true;
                    return newTag.trim();
                }
                return cleanT;
            });
            
            if (modified) {
                tags = tags.filter(t => t.length > 0);
                await FirebaseService.updateMeeting(meeting.id, { 
                    counselorTags: tags.join(', '),
                    tags: tags.join(', ')
                });
                updatedCount++;
            }
        }
        
        if (updatedCount > 0) {
            await DataManager.analyzeAndUpdateKeywords(clientId);
        }
        return updatedCount;
    },

    deleteClientTags: async (clientId, tagsToDeleteArray) => {
        const meetings = await DataManager.getClientMeetings(clientId, false);
        let updatedCount = 0;
        const lowerTagsToDelete = tagsToDeleteArray.map(t => t.toLowerCase().trim());
        
        for (const meeting of meetings) {
            const rawTags = meeting.counselorTags || meeting.tags || '';
            if (!rawTags) continue;
            
            let tags = rawTags.split(',').map(t => t.trim()).filter(Boolean);
            const initialLen = tags.length;
            
            tags = tags.filter(t => {
                let cleanT = t.replace(/[.,;:!?]+$/, '').toLowerCase().trim();
                return !lowerTagsToDelete.includes(cleanT);
            });
            
            if (tags.length !== initialLen) {
                await FirebaseService.updateMeeting(meeting.id, { 
                    counselorTags: tags.join(', '),
                    tags: tags.join(', ')
                });
                updatedCount++;
            }
        }
        
        if (updatedCount > 0) {
            await DataManager.analyzeAndUpdateKeywords(clientId);
        }
        return updatedCount;
    },

    importMeetingsBatch: async (clientId, meetings) => {
        const results = {
            success: [],
            duplicates: [],
            failed: []
        };

        // Get existing ACTIVE meetings to check duplicates
        const existingMeetings = await DataManager.getClientMeetings(clientId, false);
        const existingDates = new Set(existingMeetings.map(m => m.date.split('T')[0]));

        for (let i = 0; i < meetings.length; i++) {
            const meeting = meetings[i];
            try {
                // Basic Validation
                if (!meeting.date || !meeting.notes) {
                    throw new Error("Dati mancanti (data o note)");
                }

                const meetingDate = meeting.date.split('T')[0];

                // Duplicate Check
                if (existingDates.has(meetingDate)) {
                    results.duplicates.push({ index: i + 1, date: meetingDate });
                    continue;
                }

                // Create
                await DataManager.createMeeting(
                    clientId,
                    meeting.date,
                    meeting.notes,
                    meeting.emoji || '📝',
                    meeting.tags || '',
                    meeting.observations || {}
                );

                results.success.push({ index: i + 1, date: meetingDate });
                existingDates.add(meetingDate); // Add to set to prevent double import in same batch

            } catch (error) {
                console.error("Import error for item", i, error);
                results.failed.push({ index: i + 1, error: error.message, meeting });
            }
        }

        // Update keywords at the end if any success
        if (results.success.length > 0) {
            await DataManager.analyzeAndUpdateKeywords(clientId);
        }

        return results;
    },

    // --- ASSESSMENT (GENERIC) ---
    saveAssessment: async (clientId, assessmentData) => {
        const payload = {
            clientId,
            timestamp: new Date().toISOString(),
            ...assessmentData
        };
        return await FirebaseService.saveAssessment(payload);
    },

    saveCollectedDocument: async (clientId, docData) => {
        const payload = {
            clientId,
            type: 'toolkit_document',
            timestamp: new Date().toISOString(),
            shared: true, // Always shared with counselor of course
            content: docData
        };
        return await FirebaseService.saveWork(payload);
    },

    getAssessmentsByClient: async (clientId) => {
        return await FirebaseService.getAssessmentsByClient(clientId);
    },

    getAllAssessments: async () => {
        return await FirebaseService.getAllAssessments();
    },

    getAllOrganizations: async () => {
        return await FirebaseService.getAllOrganizations();
    },

    saveOrganization: async (orgData) => {
        return await FirebaseService.saveOrganization(orgData);
    },

    deleteOrganization: async (orgId) => {
        return await FirebaseService.deleteOrganization(orgId);
    },

    getUnassignedSomaticExperiments: async () => {
        const assessments = await FirebaseService.getAssessmentsByClient('unassigned');
        return assessments.filter(a => a.type === 'somatic_experiment');
    },

    updateAssessment: async (assessmentId, data) => {
        return await FirebaseService.updateAssessment(assessmentId, data);
    },

    deleteAssessment: async (assessmentId) => {
        return await FirebaseService.deleteAssessment(assessmentId);
    },

    // --- BASELINES & REPORTS (ASYNC) ---

    saveBaselineAssessment: async (clientId, type, anamnesisData, qualitativeAnswers, wheelScores, contractId, timestamp = null) => {
        let anamnesis = null;
        const ts = timestamp || new Date().toISOString();

        if (anamnesisData) {
            anamnesis = {};
            // Campi semplificati: solo anagrafica base e contratto
            ['eta', 'lavoro', 'contratto'].forEach(k => {
                if (anamnesisData[k]) {
                    anamnesis[k] = [{
                        value: anamnesisData[k] || "",
                        date: ts,
                        author: "Client",
                        sessionRef: null,
                        note: ""
                    }];
                }
            });
        }

        const payload = {
            type: 'baseline_' + type, // e.g. baseline_T0, baseline_checkpoint
            baselineType: type,
            clientId,
            contractId,
            anamnesis,
            qualitative: qualitativeAnswers,
            wheelOfLife: wheelScores
        };
        if (timestamp) payload.timestamp = timestamp;

        const newBaseline = await FirebaseService.saveAssessment(payload);

        return newBaseline;
    },

    updateBaseline: async (baselineId, data) => {
        return await FirebaseService.updateAssessment(baselineId, data);
    },

    deleteBaseline: async (baselineId) => {
        return await FirebaseService.deleteAssessment(baselineId);
    },

    getAllBaselines: async (clientId) => {
        // 1. Get from dedicated assessments collection
        const assessments = await FirebaseService.getAssessmentsByClient(clientId);
        let baselines = assessments.filter(a => a.type && a.type.startsWith('baseline_'));

        // 2. Get from works (in case they were saved as generic assessments or creative tools)
        const works = await FirebaseService.getWorksByClient(clientId);
        const wheelWorks = works.filter(w => w.type === 'wheel_of_life' || w.type === 'assessment' || (w.content && typeof w.content === 'object' && w.content.scores));
        
        // Convert works to baseline format if needed
        const convertedWorks = wheelWorks.map(w => ({
            id: w.id,
            timestamp: w.timestamp,
            type: 'baseline_auto',
            baselineType: w.baselineType || 'T1', // default to intermediate
            scores: w.content?.scores || w.scores || {},
            labels: w.content?.labels || w.labels || null
        }));

        const allData = [...baselines, ...convertedWorks].sort((a, b) => 
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );

        if (allData.length === 0) return [];

        // 3. Heuristic: If T0/T2 are not explicitly set, assign them chronologically
        const explicitT0 = allData.find(d => d.baselineType === 'T0');
        const explicitT2 = allData.find(d => d.baselineType === 'T2');

        if (!explicitT0 && allData.length > 0) {
            allData[0].baselineType = 'T0'; // Oldest is T0
        }
        if (!explicitT2 && allData.length > 1) {
            allData[allData.length - 1].baselineType = 'T2'; // Newest is T2
        } else if (!explicitT2 && allData.length === 1 && !explicitT0) {
            // Already handled by making it T0
        }

        return allData;
    },

    getLatestT0: async (clientId) => {
        const baselines = await DataManager.getAllBaselines(clientId);
        return baselines.find(b => b.baselineType === 'T0') || null;
    },

    getLatestT1: async (clientId) => {
        const baselines = await DataManager.getAllBaselines(clientId);
        return baselines.find(b => b.baselineType === 'T1') || null;
    },

    getLatestT2: async (clientId) => {
        const baselines = await DataManager.getAllBaselines(clientId);
        return baselines.find(b => b.baselineType === 'T2') || null;
    },

    getParameterTimeline: async (clientId, parameter) => {
        const snapshot = await DataManager.getAnamnesisSnapshot(clientId);
        return snapshot[parameter]?.history || [];
    },

    compareBaselines: async (clientId) => {
        const t0 = await DataManager.getLatestT0(clientId);
        const t1 = await DataManager.getLatestT1(clientId);

        if (!t0 || !t1) {
            return {
                hasComparison: false,
                t0: t0,
                t1: t1,
                message: t0 ? 'T1 non ancora completato' : 'T0 non disponibile'
            };
        }

        // Calculate wheel differences
        const wheelComparison = {};
        Object.keys(t0.wheelOfLife).forEach(category => {
            const t0Score = t0.wheelOfLife[category] || 0;
            const t1Score = t1.wheelOfLife[category] || 0;
            const diff = t1Score - t0Score;
            const percentChange = t0Score !== 0 ? ((diff / t0Score) * 100).toFixed(1) : 0;

            wheelComparison[category] = {
                t0: t0Score,
                t1: t1Score,
                diff: diff,
                percentChange: percentChange,
                trend: diff > 0 ? 'improvement' : diff < 0 ? 'decline' : 'stable'
            };
        });

        return {
            hasComparison: true,
            t0: t0,
            t1: t1,
            wheelComparison: wheelComparison,
            qualitativeComparison: {
                expectations: { t0: t0.qualitative?.expectations, t1: t1.qualitative?.expectations },
                resources: { t0: t0.qualitative?.resources, t1: t1.qualitative?.resources },
                suffering: { t0: t0.qualitative?.suffering, t1: t1.qualitative?.suffering },
                motivation: { t0: t0.qualitative?.motivation, t1: t1.qualitative?.motivation }
            },
            timeElapsed: Math.floor((new Date(t1.timestamp) - new Date(t0.timestamp)) / (1000 * 60 * 60 * 24))
        };
    },

    getThemeEvolution: async (clientId) => {
        const client = await DataManager.getClientById(clientId);
        if (!client) return null;

        const t0 = await DataManager.getLatestT0(clientId);
        if (!t0) return null;

        const baselineThemes = client.baselineThemes || {};
        const currentThemes = client.keywords || {};

        // Categorize themes
        const persisting = [];
        const emerged = [];
        const resolved = [];

        const baselineKeys = Object.keys(baselineThemes);
        const currentKeys = Object.keys(currentThemes);

        baselineKeys.forEach(theme => {
            if (currentThemes[theme] && currentThemes[theme].count > 2) {
                persisting.push({ theme, baselineOrigin: baselineThemes[theme], currentCount: currentThemes[theme].count });
            } else {
                resolved.push({ theme, baselineOrigin: baselineThemes[theme], currentCount: currentThemes[theme]?.count || 0 });
            }
        });

        currentKeys.forEach(theme => {
            if (!baselineThemes[theme]) {
                emerged.push({ theme, count: currentThemes[theme].count, firstSeen: currentThemes[theme].firstSeen });
            }
        });

        return {
            t0Date: t0.timestamp,
            persisting: persisting.sort((a, b) => b.currentCount - a.currentCount),
            emerged: emerged.sort((a, b) => b.count - a.count),
            resolved: resolved.sort((a, b) => b.currentCount - a.currentCount)
        };
    },

    getAnamnesisSnapshot: async (clientId) => {
        const t0 = await DataManager.getLatestT0(clientId);
        // Include tutte le chiavi utilizzate da AnamnesisTable
        const keys = ['eta', 'lavoro', 'contratto', 'hobby', 'fattoriMedici', 'noteGenerali'];
        const snapshot = {};

        keys.forEach(key => {
            snapshot[key] = {
                current: null,
                history: [],
                changeCount: 0,
                hasChanges: false
            };
        });

        if (t0 && t0.anamnesis) {
            // Unione delle chiavi base con eventuali nuove chiavi salvate nel DB
            const allKeys = new Set([...keys, ...Object.keys(t0.anamnesis)]);
            
            allKeys.forEach(key => {
                const history = t0.anamnesis[key] || [];
                // Ensure array
                const historyArray = Array.isArray(history) ? history : [history];

                // Sort history asc by date (oldest first)
                historyArray.sort((a, b) => new Date(a.date) - new Date(b.date));

                if (historyArray.length > 0) {
                    snapshot[key] = {
                        current: historyArray[historyArray.length - 1],
                        history: historyArray,
                        changeCount: historyArray.length,
                        hasChanges: historyArray.length > 1
                    };
                } else if (!snapshot[key]) {
                    snapshot[key] = {
                        current: null,
                        history: [],
                        changeCount: 0,
                        hasChanges: false
                    };
                }
            });
        }

        return snapshot;
    },

    updateAnamnesisParameter: async (clientId, parameter, value, sessionRef = null, note = "") => {
        let t0 = await DataManager.getLatestT0(clientId);

        // If T0 doesn't exist, create a stub T0 to hold the anamnesis data
        if (!t0) {
            console.log("T0 missing, creating new structure for Anamnesis update...");
            t0 = {
                clientId: clientId,
                type: 'baseline_T0',
                baselineType: 'T0',
                timestamp: new Date().toISOString(),
                anamnesis: {}
            };
            // Create the document first to get an ID
            const newDoc = await FirebaseService.saveAssessment(t0);
            t0.id = newDoc.id; // Assign the new ID
        }

        if (!t0.anamnesis) t0.anamnesis = {};

        // Normalize parameter key if needed (e.g., hobby -> hobby)
        if (!t0.anamnesis[parameter]) t0.anamnesis[parameter] = [];

        // Ensure it's an array (migration safety from legacy data)
        if (!Array.isArray(t0.anamnesis[parameter])) {
            const oldValue = t0.anamnesis[parameter];
            t0.anamnesis[parameter] = [];
            if (oldValue) {
                t0.anamnesis[parameter].push({
                    value: oldValue,
                    date: t0.timestamp,
                    author: 'Client',
                    note: 'Original entry'
                });
            }
        }

        const newEntry = {
            value: value,
            date: new Date().toISOString(),
            author: 'Counselor',
            sessionRef: sessionRef,
            note: note
        };

        // Add new entry to history
        t0.anamnesis[parameter].push(newEntry);

        // Update in Firebase
        if (t0.id) {
            return await FirebaseService.updateAssessment(t0.id, { anamnesis: t0.anamnesis });
        } else {
            // Fallback if ID is somehow still missing (shouldn't happen with create logic above)
            return await FirebaseService.saveAssessment(t0);
        }
    },

    migrateOldBaselines: async () => {
        // Stub for future data migration if needed
        return Promise.resolve();
    },

    // --- AGGREGATION & HELPERS (ASYNC/HYBRID) ---

    getCategoryAnalysis: async (clientId) => {
        const client = await DataManager.getClientById(clientId);
        const categoryMap = {
            'Lavoro': ['lavoro', 'ufficio', 'capo', 'collega', 'progetto', 'business'],
            'Famiglia': ['famiglia', 'mamma', 'papà', 'figlio', 'casa', 'genitori'],
            'Relazioni': ['amico', 'partner', 'amore', 'sociale', 'uscire'],
            'Salute': ['salute', 'corpo', 'malattia', 'stanco', 'dormire'],
            'Emozioni': ['ansia', 'paura', 'triste', 'felice', 'rabbia', 'gioia'],
            'Finanze': ['soldi', 'denaro', 'spesa', 'costo', 'pagare'],
            'Crescita': ['imparare', 'corso', 'libro', 'leggere', 'migliorare'],
            'Ambiente': ['natura', 'viaggio', 'luogo', 'città', 'mondo']
        };

        const calculateScore = (sourceMap) => {
            const scores = {};
            Object.keys(categoryMap).forEach(k => scores[k] = 2);
            if (!sourceMap) return scores;

            Object.keys(categoryMap).forEach(cat => {
                let count = 0;
                categoryMap[cat].forEach(word => {
                    if (sourceMap[word]) count += sourceMap[word].count;
                });
                if (count > 0) scores[cat] = Math.min(10, 2 + Math.ceil(Math.log2(count + 1) * 2));
            });
            return scores;
        };

        return {
            clientAuto: calculateScore(client ? client.keywords : {}),
            counselorAuto: calculateScore(client ? client.counselorKeywords : {})
        };
    },

    // Helper to update client keywords map
    updateClientKeywords: async (clientId, newTags, oldTags = [], source = 'manual') => {
        const client = await DataManager.getClientById(clientId);
        if (!client) return;

        if (!client.keywords) client.keywords = {};

        newTags.forEach(tag => {
            if (!client.keywords[tag]) {
                client.keywords[tag] = { count: 0, firstSeen: new Date().toISOString() };
            }
            client.keywords[tag].count += 1;
            client.keywords[tag].lastSeen = new Date().toISOString();
        });

        await FirebaseService.updateClient(clientId, { keywords: client.keywords });
    },

    updateCounselorKeywords: async (clientId, tagsString) => {
        const client = await DataManager.getClientById(clientId);
        if (!client) return;

        if (!client.counselorKeywords) client.counselorKeywords = {};

        const tags = tagsString.split(',').map(t => t.trim().toLowerCase()).filter(t => t);
        tags.forEach(tag => {
            if (!client.counselorKeywords[tag]) {
                client.counselorKeywords[tag] = { count: 0, firstSeen: new Date().toISOString() };
            }
            client.counselorKeywords[tag].count += 1;
            client.counselorKeywords[tag].lastSeen = new Date().toISOString();
        });

        await FirebaseService.updateClient(clientId, { counselorKeywords: client.counselorKeywords });
    },

    analyzeAndUpdateKeywords: async (clientId) => {
        const works = await FirebaseService.getWorksByClient(clientId);
        const meetings = await FirebaseService.getMeetingsByClient(clientId);

        const keywords = analyzeClientTexts(works, meetings, 60);

        await FirebaseService.updateClient(clientId, { keywords: keywords });
        return keywords;
    },

    checkMilestones: async (clientId) => {
        const meetings = await DataManager.getClientMeetings(clientId);
        const baselines = await DataManager.getAllBaselines(clientId);
        const sessionCount = meetings.length;

        // Check meeting types for phase detection
        const hasPrimoColloquio = meetings.some(m => (m.observations?.meetingType || m.meetingType) === 'primo_colloquio');
        const hasChiusura = meetings.some(m => (m.observations?.meetingType || m.meetingType) === 'chiusura_percorso');
        const checkpointCount = meetings.filter(m => ['checkpoint', 'primo_colloquio', 'chiusura_percorso'].includes(m.observations?.meetingType || m.meetingType)).length;

        let phase = 'in_corso';
        let phaseIcon = '🛋️';
        let phaseName = 'In Corso';

        if (hasChiusura) {
            phase = 'completato';
            phaseIcon = '🏁';
            phaseName = 'Percorso Completato';
        } else if (sessionCount >= 5) {
            phase = 'avanzato';
            phaseIcon = '🌿';
            phaseName = 'Percorso Avanzato';
        } else if (hasPrimoColloquio || sessionCount >= 1) {
            phase = 'avviato';
            phaseIcon = '🌱';
            phaseName = 'Percorso Avviato';
        }

        return {
            currentPhase: phase,
            phaseIcon,
            phaseName,
            sessionCount,
            checkpointCount,
            hasT0: baselines.some(b => b.baselineType === 'T0'),
            hasT2: baselines.some(b => b.baselineType === 'T2'),
            hasBaselines: baselines.length > 0,
            nextSession: sessionCount + 1,
            progressPercentage: Math.min(100, Math.max(0, (sessionCount / 10) * 100)),
            canExportReport: baselines.some(b => b.baselineType === 'T0') && baselines.some(b => b.baselineType === 'T2')
        };
    },

    // --- MIGRATION UTILS ---

    /**
     * One-off migration from LocalStorage to Firebase
     */
    migrateFromLocalStorage: async () => {
        const STORAGE_KEY = 'awareness_tool_db_v2';
        const rawData = localStorage.getItem(STORAGE_KEY);

        if (!rawData) {
            return { success: false, message: "Nessun dato locale trovato." };
        }

        try {
            const db = JSON.parse(rawData);
            const report = { clients: 0, works: 0, meetings: 0, errors: [] };

            console.log("Inizio migrazione...", db);

            // 1. Migrate Clients
            if (db.clients) {
                for (const client of db.clients) {
                    try {
                        // Check duplicate by email
                        const existing = await DataManager.getClient(client.email);
                        if (!existing) {
                            // Create client manually to preserve ID if possible or use new logic
                            // For simplicity, we create new structure but we assume ID might change unless we force it.
                            // FirebaseService createClient generates a new ID. 
                            // To keep relationships, we should probably try to keep IDs or map them.
                            // BETTER APPROACH: Use setDoc with specific ID in Service, but Service abstracts it.
                            // Fallback: We recreate the client and update references? Too complex.
                            // Let's create new clients and re-map works.

                            // NOTE: Service creates ID automatically. 
                            // We will try to create the client and use the NEW ID for linked data.

                            const newClientRef = await FirebaseService.createClient({
                                name: client.name,
                                email: client.email,
                                mode: client.mode || 'autonomous',
                                isActive: client.isActive !== false,
                                contract: client.contract,
                                keywords: client.keywords
                            });

                            const newClientId = newClientRef.id;
                            const oldClientId = client.id;
                            report.clients++;

                            // 2. Migrate Works for this client
                            const clientWorks = db.works ? db.works.filter(w => w.clientId === oldClientId) : [];
                            for (const work of clientWorks) {
                                await DataManager.saveWork(newClientId, work.type, work.content, work.shared);
                                report.works++;
                            }

                            // 3. Migrate Meetings for this client
                            const clientMeetings = db.meetings ? db.meetings.filter(m => m.clientId === oldClientId) : [];
                            for (const meeting of clientMeetings) {
                                await DataManager.createMeeting(
                                    newClientId,
                                    meeting.date,
                                    meeting.counselorNotes,
                                    meeting.counselorEmoji,
                                    meeting.counselorTags
                                );
                                report.meetings++;
                            }

                            // 4. Migrate Baselines
                            const clientBaselines = db.baselineAssessments ? db.baselineAssessments.filter(b => b.clientId === oldClientId) : [];
                            for (const baseline of clientBaselines) {
                                // Re-save baseline using new ID
                                await FirebaseService.saveAssessment({
                                    ...baseline,
                                    clientId: newClientId,
                                    // Ensure type is correct for new logic
                                    type: baseline.type.startsWith('baseline_') ? baseline.type : 'baseline_' + baseline.type,
                                    baselineType: baseline.type.replace('baseline_', '') // normalization
                                });
                            }
                        } else {
                            console.log(`Cliente ${client.email} già presente, salto.`);
                        }
                    } catch (err) {
                        console.error(`Errore migrazione cliente ${client.name}:`, err);
                        report.errors.push(`Cliente ${client.name}: ${err.message}`);
                    }
                }
            }

            return { success: true, report };
        } catch (error) {
            console.error("Errore parsing localStorage:", error);
            return { success: false, message: "Errore lettura dati locali: " + error.message };
        }
    },

    // --- COUNTERTRANSFERT (Local Storage for now) ---
    saveCountertransfer: (clientId, data) => {
        const key = `countertransfer_${clientId}`;
        localStorage.setItem(key, JSON.stringify(data));
    },

    getCountertransfer: (clientId) => {
        const key = `countertransfer_${clientId}`;
        const data = localStorage.getItem(key);
        return data ? JSON.parse(data) : null;
    },

    // --- GROUP COUNSELING REPORTS ---
    saveGroupReport: async (reportData) => {
        try {
            return await FirebaseService.saveGroupReport(reportData);
        } catch (error) {
            console.error("DataManager: error saving group report, saving locally as fallback:", error);
            const localKey = 'local_group_reports';
            const existing = JSON.parse(localStorage.getItem(localKey) || '[]');
            const newReport = {
                ...reportData,
                id: 'local_' + Date.now(),
                createdAt: new Date().toISOString()
            };
            existing.unshift(newReport);
            localStorage.setItem(localKey, JSON.stringify(existing));
            return newReport;
        }
    },

    updateGroupReport: async (reportId, reportData) => {
        try {
            return await FirebaseService.updateGroupReport(reportId, reportData);
        } catch (error) {
            console.error("DataManager: error updating group report locally:", error);
            const localKey = 'local_group_reports';
            let existing = JSON.parse(localStorage.getItem(localKey) || '[]');
            existing = existing.map(r => r.id === reportId ? { ...r, ...reportData } : r);
            localStorage.setItem(localKey, JSON.stringify(existing));
            return { id: reportId, ...reportData };
        }
    },

    updateGroupAvatar: async (groupTitle, newAvatar) => {
        const localKey = 'local_group_reports';
        let existing = JSON.parse(localStorage.getItem(localKey) || '[]');
        existing = existing.map(r => (r.title || '').trim() === groupTitle.trim() ? { ...r, groupAvatar: newAvatar } : r);
        localStorage.setItem(localKey, JSON.stringify(existing));

        // Also save avatar to localStorage group preferences
        const groupAvatars = JSON.parse(localStorage.getItem('group_avatars_map') || '{}');
        groupAvatars[groupTitle.trim()] = newAvatar;
        localStorage.setItem('group_avatars_map', JSON.stringify(groupAvatars));

        try {
            const firebaseReports = await FirebaseService.getAllGroupReports();
            const matching = firebaseReports.filter(r => (r.title || '').trim() === groupTitle.trim());
            for (const r of matching) {
                await FirebaseService.updateGroupReport(r.id, { groupAvatar: newAvatar });
            }
        } catch (e) {
            console.warn("DataManager: avatar updated locally only:", e);
        }
    },

    deleteGroupReport: async (reportId) => {
        try {
            return await FirebaseService.deleteGroupReport(reportId);
        } catch (error) {
            console.error("DataManager: error deleting group report:", error);
            const localKey = 'local_group_reports';
            let existing = JSON.parse(localStorage.getItem(localKey) || '[]');
            existing = existing.filter(r => r.id !== reportId);
            localStorage.setItem(localKey, JSON.stringify(existing));
            return true;
        }
    },

    seedSampleGroup: async () => {
        const localKey = 'local_group_reports';
        let existing = JSON.parse(localStorage.getItem(localKey) || '[]');
        const sampleIds = SAMPLE_HYPERCONNECTION_REPORTS.map(s => s.id);
        existing = existing.filter(r => !sampleIds.includes(r.id));
        existing = [...SAMPLE_HYPERCONNECTION_REPORTS, ...existing];
        localStorage.setItem(localKey, JSON.stringify(existing));
        return existing;
    },

    getAllGroupReports: async (counselorId = null) => {
        try {
            const firebaseReports = await FirebaseService.getAllGroupReports(counselorId);
            let localReports = JSON.parse(localStorage.getItem('local_group_reports') || '[]');
            
            // Seed or refresh sample reports if missing or outdated
            const sampleIds = SAMPLE_HYPERCONNECTION_REPORTS.map(s => s.id);
            const hasOldSample = localReports.some(r => r.id === 'sample_hyper_01' || r.id === 'sample_hyper_02' || r.id === 'sample_hyper_03');
            const hasAllFour = localReports.filter(r => sampleIds.includes(r.id)).length === SAMPLE_HYPERCONNECTION_REPORTS.length;

            if (firebaseReports.length === 0 && (localReports.length === 0 || (hasOldSample && !hasAllFour))) {
                const nonSample = localReports.filter(r => !r.id.startsWith('sample_hyper_'));
                localReports = [...SAMPLE_HYPERCONNECTION_REPORTS, ...nonSample];
                localStorage.setItem('local_group_reports', JSON.stringify(localReports));
            }

            // Apply saved group avatars map
            const groupAvatars = JSON.parse(localStorage.getItem('group_avatars_map') || '{}');

            // Merge and deduplicate by id
            const allMap = new Map();
            firebaseReports.forEach(r => {
                const groupTitle = (r.title || '').trim();
                const avatar = r.groupAvatar || groupAvatars[groupTitle] || (groupTitle.toLowerCase().includes('chi sei') ? '🪞' : '👥');
                allMap.set(r.id, { ...r, groupAvatar: avatar });
            });
            localReports.forEach(r => { 
                if (!allMap.has(r.id)) {
                    const groupTitle = (r.title || '').trim();
                    const avatar = r.groupAvatar || groupAvatars[groupTitle] || (groupTitle.toLowerCase().includes('chi sei') ? '🪞' : '👥');
                    allMap.set(r.id, { ...r, groupAvatar: avatar });
                }
            });
            return Array.from(allMap.values()).sort((a, b) => new Date(b.date || b.createdAt || 0) - new Date(a.date || a.createdAt || 0));
        } catch (error) {
            console.warn("DataManager: fallback to local group reports:", error);
            let localReports = JSON.parse(localStorage.getItem('local_group_reports') || '[]');
            const sampleIds = SAMPLE_HYPERCONNECTION_REPORTS.map(s => s.id);
            const hasOldSample = localReports.some(r => r.id === 'sample_hyper_01' || r.id === 'sample_hyper_02' || r.id === 'sample_hyper_03');
            const hasAllFour = localReports.filter(r => sampleIds.includes(r.id)).length === SAMPLE_HYPERCONNECTION_REPORTS.length;

            if (localReports.length === 0 || (hasOldSample && !hasAllFour)) {
                const nonSample = localReports.filter(r => !r.id.startsWith('sample_hyper_'));
                localReports = [...SAMPLE_HYPERCONNECTION_REPORTS, ...nonSample];
                localStorage.setItem('local_group_reports', JSON.stringify(localReports));
            }
            const groupAvatars = JSON.parse(localStorage.getItem('group_avatars_map') || '{}');
            return localReports.map(r => {
                const groupTitle = (r.title || '').trim();
                const avatar = r.groupAvatar || groupAvatars[groupTitle] || (groupTitle.toLowerCase().includes('chi sei') ? '🪞' : '👥');
                return { ...r, groupAvatar: avatar };
            }).sort((a, b) => new Date(b.date || b.createdAt || 0) - new Date(a.date || a.createdAt || 0));
        }
    }
};

export const SAMPLE_HYPERCONNECTION_REPORTS = [
    {
        id: 'sample_hyper_01',
        title: 'Chi sei quando nessuno ti guarda',
        sessionNumber: 'Incontro 1 di 4',
        date: '2026-09-03',
        duration: '2h 15 min (135 min)',
        location: 'Presenza - Studio',
        counselorName: 'Marilisa Del Vecchio',
        coCounselor: '',
        groupAvatar: '🪞',
        isEntity: false,
        entityName: '',
        entityReferent: '',
        entityNotes: '',
        groupObjective: "Workshop 'Chi sei quando nessuno ti guarda' (Percorso da 4 incontri, 8 ore complessive). Uno spazio protetto per esplorare la fame di riconoscimento, la retroflessione digitale nello smartphone, la maschera della performance online e il valore fecondo del vuoto fertile.",
        theme: 'La fame di riconoscimento',
        objective: 'Essere visti quando si lavora da casa: la fatica di sentirsi presenti e riconosciuti senza il contatto fisico con gli altri.',
        climate: 'Accogliente & Aperto',
        groupPhase: '1. Apertura / Orientamento & Contratto',
        selectedClientIds: [],
        externalParticipants: 'Elena Benetti, Marco Valente, Giulia Sartori, Samuele Testa, Chiara Marini',
        absentParticipants: '',
        emergedThemes: `• RIFERIMENTO TEORICO: Analisi Transazionale (Eric Berne, la fame di stimoli e riconoscimento).

• APERTURA (Solo Incontro 1): Presentazione del corso: obiettivi, struttura, patto di gruppo. Giro di presentazione dei partecipanti: solo il nome, niente altro - l'incontro resta centrato sul tema, non sulle storie personali. Per questo l'incontro 1 dura 15 minuti in più rispetto agli altri.

• APERTURA IN UNA PAROLA: "Se oggi fossi un supereroe, quale sarebbe il tuo potere?" (in una parola, con che energia arrivi).

• DOMANDE GUIDA ESPLORATE:
  1. Quando, questa settimana, hai sentito che nessuno si era accorto di quanto stavi facendo?
  2. C'è una situazione in cui hai fatto qualcosa in più solo per essere notato?

• CHIUSURA IN UNA PAROLA: "In una parola, come ti senti dopo aver parlato di essere visto?"

• APPROFONDIMENTI BLOG (spazionuvola.it):
  → Essere visti quando lavori da solo in una stanza: https://spazionuvola.it/blog/essere-visti-lavorando-da-casa
  → Perché controllo sempre il telefono senza motivo?: https://spazionuvola.it/blog/perche-controllo-il-telefono`,
        techniques: ['Giro di Condivisione (Check-in/out)', 'Respirazione, Centratura & Grounding', 'Scrittura Espressiva & Journaling', 'Rispecchiamento & Feedback Circolare'],
        insights: 'Lavorare da remoto amplifica la fame di riconoscimento: il continuo refresh delle email e dei messaggi è spesso una ricerca di segnali di esistenza e convalida relazionale.',
        homework: "Oggi abbiamo parlato di come, lavorando da casa, manchi spesso la conferma di essere visti per quello che si fa. Questa settimana il compito è solo osservare: ogni volta che controlli lo schermo aspettando un segno di riconoscimento, segna dov'eri e cosa avevi appena fatto.",
        nextSessionDate: '2026-09-10',
        nextSessionFocus: "Incontro 2: L'oggetto a cui ci aggrappiamo (smartphone come surrogato e retroflessione digitale)",
        counselorNotes: 'Avvio solido e rispettoso dei confini. Il giro di soli nomi ha protetto lo spazio mantenendo il focus sulla dinamica universale del riconoscimento.'
    },
    {
        id: 'sample_hyper_02',
        title: 'Chi sei quando nessuno ti guarda',
        sessionNumber: 'Incontro 2 di 4',
        date: '2026-09-10',
        duration: '2h (120 min)',
        location: 'Presenza - Studio',
        counselorName: 'Marilisa Del Vecchio',
        coCounselor: '',
        groupAvatar: '🪞',
        isEntity: false,
        entityName: '',
        entityReferent: '',
        entityNotes: '',
        groupObjective: "Workshop 'Chi sei quando nessuno ti guarda' (Percorso da 4 incontri, 8 ore complessive). Uno spazio protetto per esplorare la fame di riconoscimento, la retroflessione digitale nello smartphone, la maschera della performance online e il valore fecondo del vuoto fertile.",
        theme: "L'oggetto a cui ci aggrappiamo",
        objective: 'Lo smartphone come surrogato: cosa cerchiamo davvero quando lo prendiamo in mano da soli, e cosa promette di calmare.',
        climate: 'Intenso & Emozionale',
        groupPhase: '2. Confronto / Differenziazione & Resistenze',
        selectedClientIds: [],
        externalParticipants: 'Elena Benetti, Marco Valente, Giulia Sartori, Samuele Testa, Chiara Marini',
        absentParticipants: '',
        emergedThemes: `• RIFERIMENTO TEORICO: Accenno all’oggetto transizionale (Winnicott) e alla “retroflessione nel digitale” (l'energia che dovrebbe andare verso un contatto vero - una persona, un bisogno - viene rivolta su se stessi o su un oggetto sostitutivo, perché il contatto reale sembra troppo rischioso o non disponibile).

• APERTURA IN UNA PAROLA: "In una parola, a cosa ti sei aggrappato di più questa settimana?"

• DOMANDE GUIDA ESPLORATE:
  1. In che momento della giornata prendi il telefono senza nemmeno deciderlo?
  2. Se in quel momento non ci fosse, cosa mancherebbe davvero?

• CHIUSURA IN UNA PAROLA: "In una parola, cosa porti a casa oggi?"

• APPROFONDIMENTI BLOG (spazionuvola.it):
  → Lo smartphone come coperta di Linus: https://spazionuvola.it/blog/smartphone-coperta-di-linus
  → "So benissimo cosa dovrei fare, ma non ci riesco": https://spazionuvola.it/blog/so-cosa-fare-ma-non-riesco`,
        techniques: ['Giro di Condivisione (Check-in/out)', 'Carte Creative & Proiettive', 'Sedia Vuota & Dialogo Polare'],
        insights: 'Lo smartphone funge da regolatore emotivo immediato e anestetico per la noia e la solitudine; interrompere il gesto automatico permette di ricontattare il bisogno primario inascoltato.',
        homework: 'La settimana scorsa hai solo osservato quando cerchi conferma. Questa settimana andiamo un passo oltre: ogni volta che prendi il telefono "senza motivo", prima di sbloccarlo fai un respiro e chiediti cosa provavi un attimo prima. Poi, se vuoi, sbloccalo pure. Non è un esercizio di autocontrollo: è dare a quel gesto automatico un secondo di spazio in più, per vedere cosa emerge in quella pausa.',
        nextSessionDate: '2026-09-17',
        nextSessionFocus: 'Incontro 3: La maschera e la performance (chi siamo in call vs chi siamo quando nessuno ci guarda)',
        counselorNotes: 'Lavoro intenso sul tema della retroflessione. Molti hanno riconosciuto come il gesto del pollice sullo schermo sia un modo per calmare l\'agitazione corporea.'
    },
    {
        id: 'sample_hyper_03',
        title: 'Chi sei quando nessuno ti guarda',
        sessionNumber: 'Incontro 3 di 4',
        date: '2026-09-17',
        duration: '2h (120 min)',
        location: 'Presenza - Studio',
        counselorName: 'Marilisa Del Vecchio',
        coCounselor: '',
        groupAvatar: '🪞',
        isEntity: false,
        entityName: '',
        entityReferent: '',
        entityNotes: '',
        groupObjective: "Workshop 'Chi sei quando nessuno ti guarda' (Percorso da 4 incontri, 8 ore complessive). Uno spazio protetto per esplorare la fame di riconoscimento, la retroflessione digitale nello smartphone, la maschera della performance online e il valore fecondo del vuoto fertile.",
        theme: 'La maschera e la performance',
        objective: 'Chi siamo online, in una call o su un profilo, rispetto a chi siamo quando nessuno ci guarda: dove si apre la crepa.',
        climate: 'Riflessivo & Introspettivo',
        groupPhase: '3. Coesione / Fiducia & Intimità',
        selectedClientIds: [],
        externalParticipants: 'Elena Benetti, Marco Valente, Giulia Sartori, Samuele Testa, Chiara Marini',
        absentParticipants: '',
        emergedThemes: `• RIFERIMENTO TEORICO: Donald Winnicott, il "Falso Sé" come difesa funzionale per compiacere l'ambiente e proteggere la vulnerabilità del Vero Sé.

• APERTURA IN UNA PAROLA: "In una parola, che maschera indossi più spesso ultimamente?"

• DOMANDE GUIDA ESPLORATE:
  1. In quale contesto ti senti più "in scena" ultimamente?
  2. Cosa cambia, nel corpo o nell'umore, quando smetti di essere osservato?

• CHIUSURA IN UNA PAROLA: "In una parola, chi sei quando quella maschera cade?"

• APPROFONDIMENTI BLOG (spazionuvola.it):
  → Chi sei quando la videocamera si spegne: https://spazionuvola.it/blog/chi-sei-videocamera-spenta`,
        techniques: ['Giro di Condivisione (Check-in/out)', 'Role Playing & Psicodramma', 'Scrittura Espressiva & Journaling', 'Rispecchiamento & Feedback Circolare'],
        insights: 'La fatica cronica deriva dal dover mantenere costantemente attiva la maschera performativa durante le call e sui canali digitali. Disattivare la maschera restituisce energia vitale.',
        homework: 'Dopo aver osservato e aver dato spazio alla pausa, questa settimana il compito è un\'azione minima: scegli una situazione a bassa posta (una persona di cui ti fidi, un momento non decisivo) e prova a togliere consapevolmente la maschera, dicendo per una volta come stai davvero invece della risposta di scena. Non serve un gesto eclatante: basta un caso solo, piccolo, vero. Puoi farlo anche da sola, scrivendo a te stessa.',
        nextSessionDate: '2026-09-24',
        nextSessionFocus: 'Incontro 4: Il vuoto che non tolleriamo (il vuoto fertile gestaltico, congedo ed essenza del gruppo)',
        counselorNotes: 'Clima di profonda intimità e fiducia. I partecipanti hanno mostrato autenticità e risonanza nel condividere il sollievo dello spegnere la videocamera.'
    },
    {
        id: 'sample_hyper_04',
        title: 'Chi sei quando nessuno ti guarda',
        sessionNumber: 'Incontro 4 di 4',
        date: '2026-09-24',
        duration: '2h (120 min)',
        location: 'Presenza - Studio',
        counselorName: 'Marilisa Del Vecchio',
        coCounselor: '',
        groupAvatar: '🪞',
        isEntity: false,
        entityName: '',
        entityReferent: '',
        entityNotes: '',
        groupObjective: "Workshop 'Chi sei quando nessuno ti guarda' (Percorso da 4 incontri, 8 ore complessive). Uno spazio protetto per esplorare la fame di riconoscimento, la retroflessione digitale nello smartphone, la maschera della performance online e il valore fecondo del vuoto fertile.",
        theme: 'Il vuoto che non tolleriamo',
        objective: 'Il vuoto non come mancanza da riempire subito, ma come spazio da cui può nascere qualcosa: chiusura del percorso, non solo tema a sé.',
        climate: 'Coeso & Trasformativo',
        groupPhase: '5. Integrazione / Chiusura & Restituzione',
        selectedClientIds: [],
        externalParticipants: 'Elena Benetti, Marco Valente, Giulia Sartori, Samuele Testa, Chiara Marini',
        absentParticipants: '',
        emergedThemes: `• RIFERIMENTO TEORICO: Concetto gestaltico del "Vuoto Fertile" (Fritz Perls): il vuoto come luogo di gestazione creativa e contatto profondo, non come minaccia da saturare compulsivamente.

• APERTURA IN UNA PAROLA: "In una parola, cosa provi quando non hai nulla da fare?"

• DOMANDE GUIDA ESPLORATE:
  1. Qual è l'ultimo momento vuoto - un'attesa, una pausa - che hai riempito subito con lo schermo?
  2. Ripensando a tutto il percorso, quale dei temi trattati ti ha lasciato addosso qualcosa di più?

• CHIUSURA IN UNA PAROLA: "In una parola, cosa vuoi lasciare vuoto questa settimana?"

• CHIUSURA DEL PERCORSO (Solo Ultimo Incontro):
  A differenza del giro iniziale del primo incontro, qui la presentazione è completa: ogni partecipante si racconta per davvero, lasciando emergere la propria essenza, non solo il nome. È il modo in cui il gruppo si congeda, portandosi a casa qualcosa di più di quando si presentavano da sconosciuti. Chiude idealmente il percorso da 4 incontri.

• APPROFONDIMENTI BLOG (spazionuvola.it):
  → Perché non riesci a sopportare un momento vuoto: https://spazionuvola.it/blog/non-sopportare-momento-vuoto
  → Il telefono che ti ruba il sonno prima ancora che tu te ne accorga: https://spazionuvola.it/blog/telefono-ruba-sonno
  → I riti che il telefono ha sostituito senza chiedere permesso: https://spazionuvola.it/blog/riti-sostituiti-dal-telefono`,
        techniques: ['Giro di Condivisione (Check-in/out)', 'Respirazione, Centratura & Grounding', 'Scultura Corporea di Gruppo', 'Rispecchiamento & Feedback Circolare'],
        insights: 'Abitare il vuoto senza afferrare il telefono è l\'inizio della libertà e della vera presenza a se stessi.',
        homework: 'Chiudiamo il percorso con il tema del vuoto: quello che riempiamo automaticamente pur di non restarci dentro. Nei prossimi giorni lascia vuoti tre momenti che di solito riempiresti con il telefono, anche solo per un minuto, e nota cosa succede. Non c\'è un incontro successivo in cui riportarlo: tienilo per te, come primo assaggio di cosa può aprirsi in quel vuoto.',
        nextSessionDate: '',
        nextSessionFocus: 'Percorso concluso con successo (4 incontri / 8 ore totali)',
        counselorNotes: 'Chiusura di straordinaria intensità. Il giro di presentazione autentica dell\'essenza personale ha sancito il passaggio da figure in scena a persone intere.'
    }
];
